import { useState } from "react";
import type { ReactNode } from "react";
import { Mail, Send } from "lucide-react";
import { sendTestMail } from "../../api/admin.js";
import type { MailTestResult, MailTransport } from "../../api/admin.js";
import { useMailStatus } from "../../data/account.js";
import { Alert, Badge, Button, Card, CardHeader, Skeleton } from "../../ui/primitives.js";
import type { BadgeTone } from "../../ui/primitives.js";

const TRANSPORT: Record<MailTransport, { readonly label: string; readonly tone: BadgeTone; readonly text: string }> = {
  smtp: { label: "SMTP", tone: "mint", text: "Mails gehen über euren Mailserver." },
  resend: { label: "Resend", tone: "mint", text: "Mails gehen über die Resend-API." },
  console: {
    label: "Entwicklungsmodus",
    tone: "buoy",
    text: "Mails werden nicht versendet, sondern nur im Server-Log ausgegeben."
  },
  off: {
    label: "Aus",
    tone: "buoy",
    text: "Es ist kein Versand eingerichtet. Setze SMTP_HOST oder RESEND_API_KEY sowie MAIL_FROM auf dem Server."
  }
};

const NOTICES: ReadonlyArray<string> = [
  "Begrüßung bei neuem Konto (ohne Einmalpasswort)",
  "Neues Einmalpasswort ausgestellt",
  "Master-Passwort geändert",
  "Zwei-Faktor-Anmeldung ein- oder ausgeschaltet",
  "Passkey hinzugefügt oder entfernt",
  "Notfall-Kit erstellt oder ersetzt, Master-Passwort damit zurückgesetzt",
  "Tresor exportiert"
];

export function MailPanel(props: { readonly email: string }): ReactNode {
  const status = useMailStatus(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<MailTestResult | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  async function test(): Promise<void> {
    setSending(true);
    setResult(undefined);
    setError(undefined);
    try {
      setResult(await sendTestMail());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Die Testnachricht konnte nicht angefordert werden.");
    } finally {
      setSending(false);
    }
  }

  if (status.isPending) {
    return <Skeleton className="h-40" />;
  }
  if (status.error !== null) {
    return <Alert tone="danger">{status.error.message}</Alert>;
  }
  const transport = TRANSPORT[status.data.transport];

  return (
    <Card>
      <CardHeader
        icon={Mail}
        title="E-Mail-Versand"
        description="Hinweise zu Konto und Sicherheit an die Nutzer. Inhalte aus Tresoren stehen nie in einer E-Mail."
        action={<Badge tone={transport.tone}>{transport.label}</Badge>}
      />
      <div className="flex flex-col gap-4 px-5 py-4">
        <p className="text-sm text-mist">
          {transport.text}
          {status.data.from !== null && status.data.transport !== "console" ? (
            <>
              {" "}Absender: <span className="text-foam">{status.data.from}</span>
            </>
          ) : null}
        </p>

        <div>
          <p className="text-sm font-medium text-foam">Versendet werden</p>
          <ul className="mt-1.5 list-disc pl-5 text-sm leading-relaxed text-mist">
            {NOTICES.map((notice: string) => <li key={notice}>{notice}</li>)}
          </ul>
          <p className="mt-2 text-[0.8125rem] text-mist-2">
            Klappt die Zustellung nicht, läuft die Aktion trotzdem durch; der Fehler steht im Server-Log.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <Button icon={Send} variant="secondary" loading={sending} disabled={status.data.transport === "off"} onClick={(): void => void test()}>
            Testnachricht senden
          </Button>
          <span className="text-[0.8125rem] text-mist-2">an {props.email}</span>
        </div>

        {result?.ok === true ? (
          <Alert tone="success" live>
            {status.data.transport === "console"
              ? `Testnachricht an ${result.to} erstellt. Sie steht im Server-Log.`
              : `Testnachricht an ${result.to} versendet. Prüfe das Postfach (auch den Spam-Ordner).`}
          </Alert>
        ) : null}
        {result?.ok === false ? <Alert tone="danger">{result.message}</Alert> : null}
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      </div>
    </Card>
  );
}
